import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useDeferredValue,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isSameMonth,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfDay,
  parseISO,
  getHours,
  getMinutes,
  getSeconds,
  differenceInMinutes,
  addDays,
} from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { AlertCircle, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Booking } from '@/types/Booking';
import {
  computeOverlapColumns,
  hideSupersededCancellations,
  type BookingColumn,
} from '@/utils/calendarLayout';
import { Service } from '@/types/Service';
import { Contact } from '@/types/Contact';
import {
  bookingsApi,
  businessHoursApi,
  availabilityOverridesApi,
  type BusinessHour,
  type AvailabilityOverride,
} from '@/services/api';
import { AppointmentCard } from './AppointmentCard';
import { AppointmentDialog } from './AppointmentDialog';
import { AppointmentDetailsPanel } from './AppointmentDetailsPanel';
import { CalendarViewToggle, CalendarView } from './CalendarViewToggle';
import { useToast } from '@/hooks/useToast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  getEffectiveHoursForDate,
  normalizeOverrideDate,
  normalizeTimeToHHmm,
} from '@/utils/availability';
import { useDateFormat } from '@/hooks/useDateFormat';

interface AppointmentCalendarProps {
  agentId: number;
  services: Service[];
  contacts: Contact[];
  onContactCreated?: (contact: Contact) => void;
  initialBookingId?: number | null;
  onInitialBookingHandled?: () => void;
  readOnly?: boolean;
  externalBusinessHours?: BusinessHour[];
  externalAvailabilityOverrides?: AvailabilityOverride[];
  refreshToken?: number;
}

export const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  agentId,
  services,
  contacts,
  onContactCreated,
  initialBookingId,
  onInitialBookingHandled,
  readOnly = false,
  externalBusinessHours,
  externalAvailabilityOverrides,
  refreshToken = 0,
}) => {
  const { t } = useTranslation();
  const df = useDateFormat();
  const { toast } = useToast();
  const [view, setView] = useState<CalendarView>(() => {
    const saved = localStorage.getItem('calendar-view');
    return saved === 'week' || saved === 'month' ? saved : 'week';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  /** Month view: selected day for the appointment list (decoupled from visible month) */
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [localBusinessHours, setLocalBusinessHours] = useState<BusinessHour[]>(
    []
  );
  const [localAvailabilityOverrides, setLocalAvailabilityOverrides] = useState<
    AvailabilityOverride[]
  >([]);

  const businessHours = externalBusinessHours ?? localBusinessHours;
  const availabilityOverrides =
    externalAvailabilityOverrides ?? localAvailabilityOverrides;

  const weekViewScrollRef = useRef<HTMLDivElement>(null);
  const weekCalendarRef = useRef<HTMLDivElement>(null);
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<'details' | 'calendar' | null>(null);
  const scrollToBookingIdRef = useRef<number | null>(null);
  const openedInitialBookingRef = useRef<number | null>(null);
  const bookingsRef = useRef<Booking[]>([]);
  bookingsRef.current = bookings;
  const isMobile = useIsMobile();

  const handleViewChange = (v: CalendarView) => {
    setView(v);
    localStorage.setItem('calendar-view', v);
    setSelectedBooking(null);
    if (v === 'month') {
      setSelectedDay(currentDate);
    }
  };

  useEffect(() => {
    if (!readOnly) return;
    setIsDialogOpen(false);
    setEditingBooking(null);
    setSelectedDate(null);
    setSelectedTime(null);
  }, [readOnly]);

  // Fetch business hours for display range
  useEffect(() => {
    if (externalBusinessHours) return;
    businessHoursApi
      .fetchBusinessHours(agentId)
      .then(setLocalBusinessHours)
      .catch(() => setLocalBusinessHours([]));
  }, [agentId, externalBusinessHours]);

  const parseTimeToMinutes = (time: string): number => {
    const normalized = normalizeTimeToHHmm(time);
    const [h, m] = normalized.split(':');
    const hours = parseInt(h ?? '0', 10);
    const minutes = parseInt(m ?? '0', 10);
    return hours * 60 + minutes;
  };

  // Compute displayed hours from business hours/overrides, extended by out-of-hours bookings.
  const { displayHours, minHour } = useMemo(() => {
    const openHours = businessHours.filter(h => !h.is_closed);
    let minH: number;
    let maxH: number;

    const rangeFromTimes = (startTime: string, endTime: string) => {
      const startMinutes = parseTimeToMinutes(startTime);
      const endMinutes = parseTimeToMinutes(endTime);
      const startHour = Math.floor(startMinutes / 60);
      const endHour = Math.ceil(endMinutes / 60);
      minH = Math.min(minH, startHour);
      maxH = Math.max(maxH, endHour);
    };

    if (openHours.length === 0) {
      minH = 8;
      maxH = 18;
    } else {
      minH = 24;
      maxH = 0;
      openHours.forEach(h => rangeFromTimes(h.start_time, h.end_time));
      if (minH >= maxH) {
        minH = 8;
        maxH = 18;
      }
    }

    availabilityOverrides.forEach(override => {
      if (override.is_closed) return;
      rangeFromTimes(override.start_time, override.end_time);
    });

    // Extend range if any bookings fall outside business hours
    bookings.forEach(b => {
      const startHour = getHours(parseISO(b.start_time));
      const end = parseISO(b.end_time);
      const endHour = getMinutes(end) > 0 ? getHours(end) + 1 : getHours(end);
      minH = Math.min(minH, startHour);
      maxH = Math.max(maxH, endHour);
    });

    const hours = Array.from({ length: maxH - minH + 1 }, (_, i) => minH + i);
    return { displayHours: hours, minHour: minH };
  }, [availabilityOverrides, businessHours, bookings]);

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    if (view === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      };
    }
  }, [view, currentDate]);

  const dateRangeStartKey = format(dateRange.start, 'yyyy-MM-dd');
  const dateRangeEndKey = format(dateRange.end, 'yyyy-MM-dd');

  // Fetch availability overrides for current date range
  useEffect(() => {
    if (externalAvailabilityOverrides) return;
    let cancelled = false;
    availabilityOverridesApi
      .fetchByAgentAndDateRange(agentId, dateRange.start, dateRange.end)
      .then(data => {
        if (!cancelled) setLocalAvailabilityOverrides(data);
      })
      .catch(error => {
        if (!cancelled) {
          console.error('Failed to fetch availability overrides:', error);
          setLocalAvailabilityOverrides([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    dateRangeStartKey,
    dateRangeEndKey,
    externalAvailabilityOverrides,
  ]);

  // Fetch bookings for the current date range (with cancellation guard for rapid navigation)
  useEffect(() => {
    let cancelled = false;

    const fetchBookings = async () => {
      const showLoadingOverlay = bookingsRef.current.length === 0;
      if (showLoadingOverlay) setIsLoading(true);
      try {
        const data = await bookingsApi.fetchBookings(
          agentId,
          dateRange.start,
          dateRange.end
        );
        if (!cancelled) setBookings(data);
      } catch (error) {
        if (!cancelled) {
          toast({
            variant: 'destructive',
            title: t(
              'agent.calendar.errors.fetch_failed.translation',
              'Failed to Load Appointments'
            ),
            description:
              error instanceof Error
                ? error.message
                : t('common.unknown_error.translation', 'Unknown error'),
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchBookings();

    return () => {
      cancelled = true;
    };
  }, [agentId, dateRangeStartKey, dateRangeEndKey, toast, t, refreshToken]);

  useEffect(() => {
    if (!initialBookingId) {
      openedInitialBookingRef.current = null;
      return;
    }
    if (openedInitialBookingRef.current === initialBookingId) return;

    let cancelled = false;

    const openInitialBooking = async () => {
      try {
        const booking = await bookingsApi.fetchBookingById(initialBookingId);
        if (cancelled) return;

        openedInitialBookingRef.current = initialBookingId;
        setCurrentDate(parseISO(booking.start_time));
        setView('week');
        localStorage.setItem('calendar-view', 'week');
        setBookings(prev =>
          prev.some(b => b.id === booking.id) ? prev : [...prev, booking]
        );
        setSelectedBooking(booking);
        scrollToBookingIdRef.current = booking.id;
        scrollTargetRef.current = isMobile ? 'details' : 'calendar';
        onInitialBookingHandled?.();
      } catch {
        if (!cancelled) onInitialBookingHandled?.();
      }
    };

    void openInitialBooking();

    return () => {
      cancelled = true;
    };
  }, [initialBookingId, onInitialBookingHandled, isMobile]);

  // Scroll week view to a selected appointment slot (e.g. from conversation deep link)
  useEffect(() => {
    const bookingId = scrollToBookingIdRef.current;
    if (view !== 'week' || isLoading || bookingId == null) return;

    const scrollToBooking = () => {
      const scrollContainer = weekViewScrollRef.current;
      if (!scrollContainer) {
        setTimeout(scrollToBooking, 100);
        return;
      }

      if (
        scrollContainer.clientHeight === 0 ||
        scrollContainer.scrollHeight === 0
      ) {
        setTimeout(scrollToBooking, 100);
        return;
      }

      const bookingElement = scrollContainer.querySelector(
        `[data-booking-id="${bookingId}"]`
      ) as HTMLElement | null;

      if (bookingElement) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = bookingElement.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const elementTop = elementRect.top - containerRect.top + scrollTop;
        const viewportHeight = scrollContainer.clientHeight;
        const targetScroll =
          elementTop - viewportHeight / 2 + elementRect.height / 2;
        scrollContainer.scrollTop = Math.max(0, targetScroll);
        scrollToBookingIdRef.current = null;
        return;
      }

      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        const startTime = parseISO(booking.start_time);
        const targetHour = Math.max(
          minHour,
          Math.min(
            getHours(startTime),
            displayHours[displayHours.length - 1] ?? 23
          )
        );
        const hourElement = scrollContainer.querySelector(
          `[data-hour="${targetHour}"]`
        ) as HTMLElement | null;
        if (hourElement) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = hourElement.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          const elementTop = elementRect.top - containerRect.top + scrollTop;
          const viewportHeight = scrollContainer.clientHeight;
          const minuteOffset = (getMinutes(startTime) / 60) * 60;
          const targetScroll = elementTop + minuteOffset - viewportHeight / 2;
          scrollContainer.scrollTop = Math.max(0, targetScroll);
        }
      }
      scrollToBookingIdRef.current = null;
    };

    requestAnimationFrame(() => {
      setTimeout(scrollToBooking, 300);
    });
  }, [
    view,
    isLoading,
    bookings,
    selectedBooking?.id,
    displayHours,
    minHour,
    dateRange.start,
    dateRange.end,
  ]);

  // Auto-scroll to current time when week view is displayed and current date is visible
  useEffect(() => {
    if (view !== 'week' || isLoading) return;
    if (scrollToBookingIdRef.current != null) return;

    const scrollToTime = () => {
      if (!weekViewScrollRef.current) {
        // Retry if ref isn't ready yet
        setTimeout(scrollToTime, 100);
        return;
      }

      const scrollContainer = weekViewScrollRef.current;
      const now = new Date();
      const today = startOfDay(now);

      // Only auto-scroll if today is in the visible week
      const weekDays = eachDayOfInterval({
        start: dateRange.start,
        end: dateRange.end,
      });
      const isTodayVisible = weekDays.some(day => isSameDay(day, today));

      // Wait for the container to have content
      if (
        scrollContainer.clientHeight === 0 ||
        scrollContainer.scrollHeight === 0
      ) {
        // Retry after a short delay if container isn't ready
        setTimeout(scrollToTime, 100);
        return;
      }

      // Try to find the hour element and scroll to it
      let targetHour: number;
      if (!isTodayVisible) {
        targetHour = minHour;
      } else {
        const currentHour = now.getHours();
        targetHour = Math.max(
          minHour,
          Math.min(currentHour, displayHours[displayHours.length - 1] ?? 23)
        );
      }

      const hourElement = scrollContainer.querySelector(
        `[data-hour="${targetHour}"]`
      ) as HTMLElement;

      if (hourElement) {
        // Scroll the element into view, centered
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = hourElement.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const elementTop = elementRect.top - containerRect.top + scrollTop;
        const viewportHeight = scrollContainer.clientHeight;

        // Center the element in the viewport
        const targetScroll = elementTop - viewportHeight / 2;
        scrollContainer.scrollTop = Math.max(0, targetScroll);
      } else {
        // Fallback to calculated scroll position if element not found
        const rowHeight = 60;
        const viewportHeight = scrollContainer.clientHeight;
        let scrollPosition: number;

        if (!isTodayVisible) {
          scrollPosition =
            (targetHour - minHour) * rowHeight - viewportHeight / 2;
        } else {
          const currentMinute = now.getMinutes();
          const currentHourPosition = (targetHour - minHour) * rowHeight;
          const minuteOffset = (currentMinute / 60) * rowHeight;
          scrollPosition =
            currentHourPosition + minuteOffset - viewportHeight / 2;
        }

        scrollContainer.scrollTop = Math.max(0, scrollPosition);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready, then add a delay
    requestAnimationFrame(() => {
      setTimeout(scrollToTime, 300);
    });
  }, [view, dateRange.start, dateRange.end, isLoading, displayHours, minHour]); // Re-scroll when view changes, date range changes, or loading completes

  // Group bookings by date
  const bookingsByDate = useMemo(() => {
    const grouped: Record<string, Booking[]> = {};
    bookings.forEach(booking => {
      const dateKey = format(parseISO(booking.start_time), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(booking);
    });
    // Sort bookings within each day by start time
    Object.keys(grouped).forEach(dateKey => {
      // Applied here rather than in the week grid so the month dots and the
      // day list agree with it: switching view should not change which
      // appointments exist.
      grouped[dateKey] = hideSupersededCancellations(grouped[dateKey]).sort(
        (a, b) =>
          parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime()
      );
    });
    return grouped;
  }, [bookings]);

  // Get bookings for a specific date
  const getBookingsForDate = (date: Date): Booking[] => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return bookingsByDate[dateKey] || [];
  };

  const doesSlotOverlapRange = (
    slotHour: number,
    startTime: string,
    endTime: string
  ): boolean => {
    const slotStart = slotHour * 60;
    const slotEnd = slotStart + 60;
    const start = parseTimeToMinutes(startTime);
    const end = parseTimeToMinutes(endTime);
    return slotStart < end && slotEnd > start;
  };

  // Calculate position and height for an appointment in pixels (relative to display start)
  const getAppointmentStyle = (
    booking: Booking,
    dayIndex: number,
    column: BookingColumn = { index: 0, count: 1 }
  ): React.CSSProperties => {
    const startTime = parseISO(booking.start_time);
    const endTime = parseISO(booking.end_time);

    // Calculate minutes from midnight
    const startMinutes = getHours(startTime) * 60 + getMinutes(startTime);
    const durationMinutes = differenceInMinutes(endTime, startTime);

    // Each hour is 60px, so each minute is 1px; offset by minHour so grid starts at business hours
    const rowHeight = 60;
    const pixelsPerMinute = rowHeight / 60; // 1px per minute
    const offsetMinutes = minHour * 60;
    const maxDisplayMinutes =
      (displayHours[displayHours.length - 1] ?? 23) * 60 + 59;

    let top = (startMinutes - offsetMinutes) * pixelsPerMinute;
    let height = Math.max(22, durationMinutes * pixelsPerMinute);
    // Clamp to visible range - appointments outside business hours are clipped
    if (top < 0) {
      height += top; // Reduce height by amount above view
      top = 0;
    }
    const bottom = top + height;
    const maxBottom = (maxDisplayMinutes - offsetMinutes + 1) * pixelsPerMinute;
    if (bottom > maxBottom) {
      height = Math.max(22, maxBottom - top);
    }

    // Calculate left position: skip first column (time labels), then each day is 12.5% width
    const dayWidth = 12.5;
    const columnWidth = dayWidth / column.count;
    const leftPercent =
      dayWidth + dayIndex * dayWidth + column.index * columnWidth;

    return {
      position: 'absolute',
      top: `${top}px`,
      height: `${height}px`,
      left: `${leftPercent}%`,
      width: `calc(${columnWidth}% - 2px)`, // Account for borders
      zIndex: 10,
      padding: '2px',
    };
  };

  // Check if current time is in visible week
  const isCurrentTimeVisible = useMemo(() => {
    if (view !== 'week') return false;
    const now = new Date();
    const today = startOfDay(now);
    const weekDays = eachDayOfInterval({
      start: dateRange.start,
      end: dateRange.end,
    });
    return weekDays.some(day => isSameDay(day, today));
  }, [view, dateRange.start, dateRange.end]);

  // Calculate current time position for indicator line (relative to display start)
  const getCurrentTimePosition = (): number | null => {
    if (!isCurrentTimeVisible) return null;
    const now = new Date();
    const totalMinutes =
      getHours(now) * 60 + getMinutes(now) + getSeconds(now) / 60;
    const maxMinutes = (displayHours[displayHours.length - 1] ?? 23) * 60 + 59;
    if (totalMinutes < minHour * 60 || totalMinutes > maxMinutes) return null;
    const pixelsPerMinute = 60 / 60; // 1px per minute
    return (totalMinutes - minHour * 60) * pixelsPerMinute;
  };

  // Get bookings for agenda (today and upcoming)
  const agendaBookings = useMemo(() => {
    const today = startOfDay(new Date());
    return bookings
      .filter(booking => parseISO(booking.start_time) >= today)
      .sort(
        (a, b) =>
          parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime()
      )
      .slice(0, 10); // Show next 10 appointments
  }, [bookings]);

  const handlePrevious = () => {
    if (view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      const nextMonth = subMonths(currentDate, 1);
      setCurrentDate(nextMonth);
      setSelectedDay(nextMonth);
    }
  };

  const handleNext = () => {
    if (view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      const nextMonth = addMonths(currentDate, 1);
      setCurrentDate(nextMonth);
      setSelectedDay(nextMonth);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDay(today);
  };

  const queueScrollToDetailsPanel = () => {
    if (!isMobile) return;
    scrollTargetRef.current = 'details';
  };

  const queueScrollToWeekCalendar = () => {
    if (!isMobile || view !== 'week') return;
    scrollTargetRef.current = 'calendar';
  };

  useEffect(() => {
    const target = scrollTargetRef.current;
    if (!isMobile || !target || isLoading) return;

    const scrollToTarget = () => {
      const element =
        target === 'details'
          ? detailsPanelRef.current
          : weekCalendarRef.current;
      if (!element) return false;

      const mainScroller = element.closest('main');
      if (mainScroller) {
        const top =
          element.getBoundingClientRect().top -
          mainScroller.getBoundingClientRect().top +
          mainScroller.scrollTop;
        mainScroller.scrollTo({
          top: Math.max(0, top - 16),
          behavior: 'smooth',
        });
        return true;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollToTarget()) {
          scrollTargetRef.current = null;
        }
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedDay, selectedBooking, isMobile, view, isLoading]);

  const handleMonthDayClick = (day: Date) => {
    setSelectedDay(day);
    setSelectedBooking(null);
    if (!isSameMonth(day, currentDate)) {
      setCurrentDate(day);
    }
    queueScrollToDetailsPanel();
  };

  const handleAppointmentClick = (booking: Booking) => {
    setSelectedBooking(booking);
    if (view === 'week') {
      scrollToBookingIdRef.current = booking.id;
    }
    queueScrollToDetailsPanel();
  };

  const handleEditAppointment = () => {
    if (readOnly || !selectedBooking) return;
    setEditingBooking(selectedBooking);
    setIsDialogOpen(true);
    setSelectedBooking(null);
  };

  const handleCloseSidebarDetails = () => {
    queueScrollToWeekCalendar();
    setSelectedBooking(null);
  };

  const handleCancelAppointment = async () => {
    if (readOnly || !selectedBooking) return;

    try {
      await bookingsApi.updateBooking(selectedBooking.id, {
        status: 'cancelled',
      });
      toast({
        title: t(
          'agent.calendar.appointment.cancelled.translation',
          'Appointment Cancelled'
        ),
      });
      queueScrollToWeekCalendar();
      setSelectedBooking(null);
      const data = await bookingsApi.fetchBookings(
        agentId,
        dateRange.start,
        dateRange.end
      );
      setBookings(data);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.cancel_failed.translation',
          'Cancel Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    }
  };

  const handleDeleteAppointment = async () => {
    if (readOnly || !selectedBooking) return;

    try {
      await bookingsApi.deleteBooking(selectedBooking.id);
      toast({
        title: t(
          'agent.calendar.appointment.deleted.translation',
          'Appointment Deleted'
        ),
      });
      queueScrollToWeekCalendar();
      setSelectedBooking(null);
      const data = await bookingsApi.fetchBookings(
        agentId,
        dateRange.start,
        dateRange.end
      );
      setBookings(data);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.calendar.appointment.errors.delete_failed.translation',
          'Delete Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    }
  };

  const handleTimeSlotClick = (date: Date, hour: number) => {
    if (readOnly) return;
    setSelectedDate(date);
    setSelectedTime(hour);
    setEditingBooking(null);
    setSelectedBooking(null);
    setIsDialogOpen(true);
  };

  const handleCreateNew = () => {
    if (readOnly) return;
    setEditingBooking(null);
    setSelectedBooking(null);
    setSelectedDate(new Date());
    setSelectedTime(null);
    setIsDialogOpen(true);
  };

  const handleSaveBooking = async (
    bookingData:
      | Parameters<typeof bookingsApi.createBooking>[0]
      | Parameters<typeof bookingsApi.updateBooking>[1]
  ) => {
    if (readOnly) return;
    try {
      if (editingBooking) {
        await bookingsApi.updateBooking(
          editingBooking.id,
          bookingData as Parameters<typeof bookingsApi.updateBooking>[1]
        );
      } else {
        await bookingsApi.createBooking(
          bookingData as Parameters<typeof bookingsApi.createBooking>[0]
        );
      }
      // Refresh bookings
      const data = await bookingsApi.fetchBookings(
        agentId,
        dateRange.start,
        dateRange.end
      );
      setBookings(data);
      setIsDialogOpen(false);
      setEditingBooking(null);
      setSelectedBooking(null);
    } catch (error) {
      throw error; // Let dialog handle the error toast
    }
  };

  // Week view
  const renderWeekView = () => {
    const weekDays = eachDayOfInterval({
      start: dateRange.start,
      end: dateRange.end,
    });

    return (
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Week Calendar */}
        <div className="flex-1">
          <Card ref={weekCalendarRef} className="relative scroll-mt-4 p-0">
            <div className="overflow-hidden rounded-xl">
              {/* Header with days */}
              <div className="relative z-0 grid grid-cols-8 border-b border-border bg-card">
                <div className="border-r border-border p-3"></div>
                {weekDays.map((day, index) => (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      'border-r border-border p-3 text-center',
                      index === weekDays.length - 1 && 'border-r-0',
                      isSameDay(day, new Date()) &&
                        'bg-blue-50 dark:bg-blue-900/20'
                    )}
                  >
                    <div className="mb-1 text-xs text-muted-foreground">
                      {df.weekdayShort(day)}
                    </div>
                    <div className="text-lg font-semibold">
                      {df.dayOfMonth(day)}
                    </div>
                    {availabilityOverrides.some(
                      o =>
                        normalizeOverrideDate(o.date) ===
                        format(day, 'yyyy-MM-dd')
                    ) && (
                      <div className="mt-1 text-[10px] font-medium text-amber-600/80 dark:text-amber-400/70">
                        {t(
                          'agent.calendar.week.override_badge.translation',
                          'Override'
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Time slots */}
              <div
                ref={weekViewScrollRef}
                className="overflow-y-auto max-h-[600px] relative"
                style={{ height: `${displayHours.length * 60}px` }}
              >
                {/* Vertical dividers in time slots area - positioned to align with header */}
                <div
                  className="absolute top-0 left-0 right-0 pointer-events-none z-10"
                  style={{ height: `${displayHours.length * 60}px` }}
                >
                  <div className="grid grid-cols-8 h-full">
                    <div className="border-r border-border"></div>
                    {weekDays.map((day, index) => (
                      <div
                        key={`time-divider-${day.toISOString()}`}
                        className={cn(
                          'border-r border-border',
                          index === weekDays.length - 1 && 'border-r-0'
                        )}
                      />
                    ))}
                  </div>
                </div>
                {/* Current time indicator line */}
                {isCurrentTimeVisible && getCurrentTimePosition() !== null && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{
                      top: `${getCurrentTimePosition()}px`,
                    }}
                  >
                    <div className="flex items-center h-0">
                      <div className="w-[12.5%] flex items-center justify-end pr-2">
                        <div className="h-2 w-2 rounded-full border-2 border-background bg-red-500"></div>
                      </div>
                      <div className="flex-1 h-0.5 bg-red-500"></div>
                    </div>
                  </div>
                )}

                {/* Hour grid lines */}
                {displayHours.map(hour => (
                  <div
                    key={hour}
                    data-hour={hour}
                    className="absolute left-0 right-0 border-b border-border"
                    style={{
                      top: `${(hour - minHour) * 60}px`,
                      height: '60px',
                    }}
                  >
                    <div className="grid grid-cols-8 h-full">
                      <div className="flex items-center bg-card p-3 text-xs font-medium text-muted-foreground">
                        {df.time(new Date().setHours(hour, 0, 0, 0))}
                      </div>
                      {weekDays.map(day => {
                        const dateStr = format(day, 'yyyy-MM-dd');
                        const overrideForDay = availabilityOverrides.find(
                          o => normalizeOverrideDate(o.date) === dateStr
                        );
                        const effective = getEffectiveHoursForDate(
                          dateStr,
                          businessHours,
                          availabilityOverrides
                        );
                        const jsDay = day.getDay();
                        const dayOfWeek = jsDay === 0 ? 7 : jsDay;
                        const businessHoursForDay = businessHours.find(
                          h => h.day_of_week === dayOfWeek
                        );
                        const hasOverride = Boolean(overrideForDay);
                        const isClosedOverride = Boolean(
                          overrideForDay?.is_closed
                        );

                        let isInRegularBusinessHours = false;
                        if (
                          businessHoursForDay &&
                          !businessHoursForDay.is_closed
                        ) {
                          isInRegularBusinessHours = doesSlotOverlapRange(
                            hour,
                            businessHoursForDay.start_time,
                            businessHoursForDay.end_time
                          );
                        }

                        let isInEffectiveHours = false;
                        if (effective && !effective.is_closed) {
                          isInEffectiveHours = doesSlotOverlapRange(
                            hour,
                            effective.start_time,
                            effective.end_time
                          );
                        }

                        const isExtendedByOverride =
                          hasOverride &&
                          !isClosedOverride &&
                          isInEffectiveHours &&
                          !isInRegularBusinessHours;
                        const isReducedByOverride =
                          hasOverride &&
                          !isClosedOverride &&
                          !isInEffectiveHours &&
                          isInRegularBusinessHours;

                        return (
                          <div
                            key={`${day.toISOString()}-${hour}`}
                            className={cn(
                              readOnly
                                ? 'cursor-default transition-colors relative'
                                : 'cursor-pointer transition-colors relative',
                              isClosedOverride
                                ? 'bg-amber-50/70 dark:bg-amber-950/20 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                                : isExtendedByOverride
                                  ? 'bg-[repeating-linear-gradient(135deg,rgba(255,251,235,0.85)_0px,rgba(255,251,235,0.85)_10px,#ffffff_10px,#ffffff_20px)] dark:bg-[repeating-linear-gradient(135deg,rgba(245,158,11,0.12)_0px,rgba(245,158,11,0.12)_10px,rgba(15,23,42,0.35)_10px,rgba(15,23,42,0.35)_20px)] hover:bg-amber-50/30 dark:hover:bg-amber-950/15'
                                  : isReducedByOverride
                                    ? 'bg-[repeating-linear-gradient(135deg,rgba(148,163,184,0.22)_0px,rgba(148,163,184,0.22)_10px,rgba(255,251,235,0.5)_10px,rgba(255,251,235,0.5)_20px)] dark:bg-[repeating-linear-gradient(135deg,rgba(51,65,85,0.55)_0px,rgba(51,65,85,0.55)_10px,rgba(245,158,11,0.1)_10px,rgba(245,158,11,0.1)_20px)] hover:bg-muted/80'
                                    : isInEffectiveHours
                                      ? 'bg-card hover:bg-emerald-50/80 dark:hover:bg-emerald-900/30'
                                      : 'bg-muted hover:bg-muted/80'
                            )}
                            onClick={
                              readOnly
                                ? undefined
                                : () => handleTimeSlotClick(day, hour)
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Appointments positioned absolutely */}
                {weekDays.map((day, dayIndex) => {
                  const dayBookings = getBookingsForDate(day);
                  const columns = computeOverlapColumns(dayBookings);
                  return dayBookings.map(booking => {
                    const service = services.find(
                      s => s.id === booking.service_id
                    );
                    const contact = contacts.find(
                      c => c.id === booking.contact_id
                    );
                    const isSelected = selectedBooking?.id === booking.id;
                    return (
                      <div
                        key={booking.id}
                        data-booking-id={booking.id}
                        style={{
                          ...getAppointmentStyle(
                            booking,
                            dayIndex,
                            columns.get(booking.id)
                          ),
                          zIndex: isSelected ? 25 : 10,
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <AppointmentCard
                          booking={booking}
                          service={service}
                          contact={contact}
                          variant="slot"
                          isSelected={isSelected}
                          onClick={e => {
                            e?.stopPropagation();
                            handleAppointmentClick(booking);
                          }}
                          className="h-full"
                        />
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Agenda / appointment details sidebar */}
        <Card
          ref={detailsPanelRef}
          className="w-full scroll-mt-4 max-lg:scroll-mb-24 p-4 lg:w-80"
        >
          {selectedBooking ? (
            <AppointmentDetailsPanel
              booking={selectedBooking}
              service={services.find(s => s.id === selectedBooking.service_id)}
              contact={contacts.find(c => c.id === selectedBooking.contact_id)}
              onEdit={handleEditAppointment}
              onCancel={handleCancelAppointment}
              onDelete={handleDeleteAppointment}
              onClose={handleCloseSidebarDetails}
              showCloseButton
              compact
              readOnly={readOnly}
            />
          ) : (
            <>
              <CardTitle className="mb-4">
                {t('agent.calendar.agenda.title.translation', 'Upcoming')}
              </CardTitle>
              <div className="space-y-2">
                {agendaBookings.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    {t(
                      'agent.calendar.agenda.empty.translation',
                      'No upcoming appointments'
                    )}
                  </div>
                ) : (
                  agendaBookings.map(booking => {
                    const service = services.find(
                      s => s.id === booking.service_id
                    );
                    const contact = contacts.find(
                      c => c.id === booking.contact_id
                    );
                    return (
                      <AppointmentCard
                        key={booking.id}
                        booking={booking}
                        service={service}
                        contact={contact}
                        contacts={contacts}
                        showDate
                        onClick={() => handleAppointmentClick(booking)}
                        className="border border-border/80"
                      />
                    );
                  })
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    );
  };

  const deferredSelectedDay = useDeferredValue(selectedDay);

  // Month view: all appointments for the selected day, sorted by time (use deferred date for smoother transition)
  const selectedDayAppointments = useMemo(() => {
    return getBookingsForDate(deferredSelectedDay).sort(
      (a, b) =>
        parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime()
    );
  }, [deferredSelectedDay, bookingsByDate]);

  // Build month grid: array of weeks, each week is an array of 7 dates (Sun–Sat)
  const monthGrid = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const allDays = eachDayOfInterval({ start: calStart, end: calEnd });
    const weeks: Date[][] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }
    return weeks;
  }, [currentDate]);

  // Built from real dates rather than literals so the names follow the
  // active language. Index 0 is Sunday, matching the grid below, which
  // starts its weeks on Sunday (weekStartsOn: 0).
  const weekdayLabels = useMemo(() => {
    const reference = startOfWeek(new Date(), { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, index) =>
      // Narrow form ('Su Mo Tu' / 'Sv P O T C Pk S'): the abbreviated
      // Latvian names run to eight characters ('ceturtd.') and will not sit
      // in a seven-column header on a narrow viewport.
      df.weekdayNarrow(addDays(reference, index))
    );
  }, [df]);

  // Month view
  const renderMonthView = () => {
    const today = new Date();

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
        {/* Calendar - 2/3 width */}
        <div
          className={cn(
            'lg:col-span-2 min-w-0 flex',
            readOnly && 'pointer-events-none select-none'
          )}
          aria-disabled={readOnly || undefined}
        >
          <Card className="flex min-w-0 flex-1 flex-col p-0">
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl">
              {/* Weekday header */}
              <div className="grid grid-cols-7 border-b border-border bg-card">
                {weekdayLabels.map((label, i) => (
                  <div
                    key={label}
                    className={cn(
                      'p-3 text-center text-xs font-medium text-muted-foreground',
                      i < 6 && 'border-r border-border'
                    )}
                  >
                    {label}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              {monthGrid.map((week, weekIdx) => (
                <div
                  key={weekIdx}
                  className={cn(
                    'grid grid-cols-7',
                    weekIdx < monthGrid.length - 1 && 'border-b border-border'
                  )}
                >
                  {week.map((day, dayIdx) => {
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, today);
                    const isSelectedDay = isSameDay(day, selectedDay);
                    const isHighlightedBookingDay =
                      selectedBooking != null &&
                      isSameDay(day, parseISO(selectedBooking.start_time));
                    const dayBookings = getBookingsForDate(day);
                    const hasActive = dayBookings.some(
                      b => (b.status ?? '').toLowerCase() !== 'cancelled'
                    );
                    const hasCancelled = dayBookings.some(
                      b => (b.status ?? '').toLowerCase() === 'cancelled'
                    );

                    const dateStr = format(day, 'yyyy-MM-dd');
                    const effective = getEffectiveHoursForDate(
                      dateStr,
                      businessHours,
                      availabilityOverrides
                    );
                    const hasOverride = availabilityOverrides.some(
                      o => normalizeOverrideDate(o.date) === dateStr
                    );
                    const isClosedDay = effective?.is_closed === true;

                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          'p-2 text-center transition-colors relative min-h-[4rem]',
                          readOnly ? 'cursor-default' : 'cursor-pointer',
                          dayIdx < 6 && 'border-r border-border',
                          isSelectedDay && 'bg-muted',
                          !readOnly &&
                            !isSelectedDay &&
                            (isClosedDay
                              ? 'bg-muted/70 hover:bg-muted'
                              : 'hover:bg-muted/50'),
                          isToday &&
                            !isSelectedDay &&
                            'bg-blue-50 dark:bg-blue-900/20',
                          hasOverride &&
                            !isClosedDay &&
                            'ring-1 ring-amber-200/90 dark:ring-amber-500/25',
                          isHighlightedBookingDay &&
                            'ring-2 ring-blue-400/70 dark:ring-blue-500/60 z-10'
                        )}
                        onClick={
                          readOnly ? undefined : () => handleMonthDayClick(day)
                        }
                      >
                        <span
                          className={cn(
                            'text-sm',
                            isSelectedDay && 'font-bold',
                            isCurrentMonth
                              ? 'text-foreground'
                              : 'text-muted-foreground'
                          )}
                        >
                          {df.dayOfMonth(day)}
                        </span>
                        {isClosedDay && (
                          <div className="mt-1 text-[10px] font-medium text-red-500 dark:text-red-400">
                            {hasOverride
                              ? t(
                                  'agent.calendar.month.closed_override.translation',
                                  'Closed (override)'
                                )
                              : t(
                                  'agent.calendar.month.closed_business_hours.translation',
                                  'Closed'
                                )}
                          </div>
                        )}
                        {!isClosedDay && hasOverride && (
                          <div className="mt-1 text-[10px] font-medium text-amber-600/80 dark:text-amber-400/70">
                            {t(
                              'agent.calendar.month.override_badge.translation',
                              'Override hours'
                            )}
                          </div>
                        )}
                        {(hasActive || hasCancelled) && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {hasActive && (
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 dark:bg-green-400" />
                            )}
                            {hasCancelled && (
                              <div className="w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Appointment list / details sidebar */}
        <Card
          ref={detailsPanelRef}
          className="min-w-0 scroll-mt-4 max-lg:scroll-mb-24 p-4 lg:col-span-1"
        >
          {selectedBooking ? (
            <AppointmentDetailsPanel
              booking={selectedBooking}
              service={services.find(s => s.id === selectedBooking.service_id)}
              contact={contacts.find(c => c.id === selectedBooking.contact_id)}
              onEdit={handleEditAppointment}
              onCancel={handleCancelAppointment}
              onDelete={handleDeleteAppointment}
              onClose={handleCloseSidebarDetails}
              showCloseButton
              compact
              readOnly={readOnly}
            />
          ) : (
            <>
              <CardTitle className="mb-4">
                {df.dateWithWeekday(deferredSelectedDay)}
              </CardTitle>
              <div className="space-y-3 overflow-y-auto max-h-[400px] min-h-[200px]">
                {selectedDayAppointments.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    {t(
                      'agent.calendar.month.no_appointments.translation',
                      'No Appointments'
                    )}
                  </div>
                ) : (
                  selectedDayAppointments.map(booking => {
                    const service = services.find(
                      s => s.id === booking.service_id
                    );
                    const contact = contacts.find(
                      c => c.id === booking.contact_id
                    );
                    return (
                      <AppointmentCard
                        key={booking.id}
                        booking={booking}
                        service={service}
                        contact={contact}
                        contacts={contacts}
                        onClick={() => handleAppointmentClick(booking)}
                        className="cursor-pointer"
                      />
                    );
                  })
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-6">
      {readOnly && (
        <Card className="rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t(
                'agent.calendar.readonly',
                'The calendar is read-only. Subscribe to create or manage appointments.'
              )}
            </p>
          </div>
        </Card>
      )}
      <div className="flex flex-col gap-3">
        <div className="flex w-full items-center justify-between lg:hidden">
          <CalendarViewToggle view={view} onViewChange={handleViewChange} />
          {!readOnly && (
            <Button variant="brand" onClick={handleCreateNew} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              {t('agent.calendar.create.translation', 'New Appointment')}
            </Button>
          )}
        </div>

        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              {t('agent.calendar.today.translation', 'Today')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="text-base font-semibold sm:ml-2">
              {view === 'week'
                ? df.dateRange(dateRange.start, dateRange.end)
                : df.monthYear(currentDate)}
            </div>
          </div>
          <div className="hidden items-center gap-2 lg:flex">
            <CalendarViewToggle view={view} onViewChange={handleViewChange} />
            {!readOnly && (
              <Button variant="brand" onClick={handleCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                {t('agent.calendar.create.translation', 'New Appointment')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="relative w-full">
        {isLoading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/70 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="text-sm text-muted-foreground">
              {t(
                'agent.calendar.loading.translation',
                'Loading appointments...'
              )}
            </div>
          </div>
        )}
        {view === 'week' ? renderWeekView() : renderMonthView()}
      </div>

      {!readOnly && (
        <AppointmentDialog
          key={
            editingBooking ? `edit-${editingBooking.id}` : `create-${agentId}`
          }
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingBooking(null);
            setSelectedBooking(null);
            setSelectedDate(null);
            setSelectedTime(null);
          }}
          onSave={handleSaveBooking}
          booking={editingBooking}
          services={services}
          contacts={contacts}
          agentId={agentId}
          initialDate={selectedDate}
          initialHour={selectedTime}
          businessHours={businessHours}
          availabilityOverrides={availabilityOverrides}
          onContactCreated={onContactCreated}
        />
      )}
    </div>
  );
};
